import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { AuthCard, AuthError, authButtonCls, authInputCls, authLabelCls } from "@/components/AuthCard";
import { useAuth } from "@/contexts/AuthContext";
import {
  PROVIDER_LABELS,
  completeSignup,
  getPendingSignup,
  getSignupOffer,
  type DataTier,
  type PendingSignup,
} from "@/lib/auth";
import { TermsModal } from "@/components/TermsModal";
import { CollaborativeConsentDocument, PriorityAddendumDocument, TermsDocument } from "@/components/TermsDocument";
import { BOOK_A_CALL_URL, PRIVACY_POLICY_URL } from "@/lib/legal";

type Doc = "terms" | "addendum" | "consent";

const DOC_TITLES: Record<Doc, string> = {
  terms: "Terms of Service",
  addendum: "Priority Addendum",
  consent: "Collaborative data consent",
};

const DOC_BODIES: Record<Doc, ReactNode> = {
  terms: <TermsDocument />,
  addendum: <PriorityAddendumDocument />,
  consent: <CollaborativeConsentDocument />,
};

const DATA_TIERS: { id: DataTier; label: string; rate: string; hint: string }[] = [
  {
    id: "privacy",
    label: "Privacy",
    rate: "25% of savings",
    hint: "Phantm doesn't keep your prompts or responses in your request logs.",
  },
  {
    id: "collaborative",
    label: "Collaborative",
    rate: "18% of savings",
    hint: "Phantm keeps prompts and responses for 90 days and uses them to improve its routing.",
  },
];

/** The offer for this signup's proven address: null for none. */
async function loadOffer(signupToken: string | null): Promise<{ benchmark_days: number } | null> {
  try {
    return (await getSignupOffer(signupToken)).priority;
  } catch {
    // Finish signup still works; the server refuses it if the address is approved.
    return null;
  }
}

/** /signup/finish: company name and the Terms, then the account exists and
 * the person is signed in as its admin. An email signup arrives with the
 * verified code's token; a GitHub signup arrives as ``?via=github``, its
 * pending signup held server-side behind a cookie. An address Phantm approved
 * for Priority (ADR-0004) also chooses its data terms and accepts the Priority
 * Addendum, and the Collaborative data consent for Collaborative. */
export function SignupFinish() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { applySession } = useAuth();
  const signupToken = (location.state as { signupToken?: string } | null)?.signupToken;
  const via = signupToken ? null : params.get("via");
  // undefined while loading; null when the GitHub signup has expired.
  const [pending, setPending] = useState<PendingSignup | null | undefined>(undefined);
  // undefined while loading; null when there is no Priority offer.
  const [priority, setPriority] = useState<{ benchmark_days: number } | null | undefined>(undefined);
  const [company, setCompany] = useState("");
  const [accepted, setAccepted] = useState<Record<Doc, boolean>>({ terms: false, addendum: false, consent: false });
  const [dataTier, setDataTier] = useState<DataTier | null>(null);
  const [reading, setReading] = useState<Doc | null>(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (via) {
      getPendingSignup().then(async (found) => {
        setPending(found);
        if (found?.company_hint) setCompany(found.company_hint);
        if (found) setPriority(await loadOffer(null));
      });
    } else if (signupToken) {
      loadOffer(signupToken).then(setPriority);
    }
  }, [via, signupToken]);

  if (via && pending === undefined) {
    return <AuthCard title="Finish signing up">{null}</AuthCard>;
  }

  if (via && pending === null) {
    return (
      <AuthCard title="Finish signing up">
        <p className="text-center text-sm text-fg-muted">
          This signup has expired.{" "}
          <Link to="/signup" className="text-electric-blue hover:underline">
            Start again
          </Link>
          .
        </p>
      </AuthCard>
    );
  }

  if (!signupToken && !pending) {
    return (
      <AuthCard title="Finish signing up">
        <p className="text-center text-sm text-fg-muted">
          This step needs a verified code.{" "}
          <Link to="/signup" className="text-electric-blue hover:underline">
            Start again
          </Link>
          .
        </p>
      </AuthCard>
    );
  }

  if (priority === undefined) {
    return <AuthCard title="Finish signing up">{null}</AuthCard>;
  }

  const docs: Doc[] = priority
    ? dataTier === "collaborative"
      ? ["terms", "addendum", "consent"]
      : ["terms", "addendum"]
    : ["terms"];
  const canSubmit =
    company.trim() !== "" && docs.every((doc) => accepted[doc]) && (!priority || dataTier !== null) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      const user =
        priority && dataTier
          ? await completeSignup(signupToken ?? null, company.trim(), true, {
              dataTier,
              acceptPriorityAddendum: accepted.addendum,
              acceptCollaborativeConsent: dataTier === "collaborative" && accepted.consent,
            })
          : await completeSignup(signupToken ?? null, company.trim(), true);
      applySession(user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't finish signing up");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Finish signing up"
      subtitle={
        pending ? (
          <>
            Signing up as <span className="text-fg">{pending.email}</span> with{" "}
            {PROVIDER_LABELS.get(pending.provider) ?? pending.provider}.
          </>
        ) : (
          "One last step."
        )
      }
      onSubmit={handleSubmit}
    >
      <AuthError message={error} />
      <div className="space-y-4">
        {priority && (
          <div data-testid="priority-offer" className="space-y-1 rounded-md border border-electric-blue/40 bg-electric-blue/5 px-3 py-3">
            <p className="text-sm font-medium text-fg">You're approved for Priority</p>
            <p className="text-sm text-fg-muted">
              You get a free {priority.benchmark_days}-day Benchmark, starting with your first request. Your app works
              exactly as it does today while Phantm measures how much it would save you.
            </p>
            <p className="text-sm text-fg-muted">
              When it ends, download your responses side by side with Phantm's to compare, then decide whether to keep
              going.
            </p>
          </div>
        )}
        <div className="space-y-2">
          <label htmlFor="company" className={authLabelCls}>Company name</label>
          <input
            id="company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            autoComplete="organization"
            autoFocus
            maxLength={200}
            className={authInputCls}
          />
        </div>
        {priority && (
          <fieldset className="space-y-2">
            <legend className={`${authLabelCls} mb-2`}>Your data</legend>
            {DATA_TIERS.map((tier) => (
              <label
                key={tier.id}
                className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 ${
                  dataTier === tier.id ? "border-electric-blue" : "border-line"
                }`}
              >
                <input
                  type="radio"
                  name="data-tier"
                  value={tier.id}
                  checked={dataTier === tier.id}
                  onChange={() => setDataTier(tier.id)}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm text-fg">
                    {tier.label} · {tier.rate}
                  </span>
                  <span className="block text-xs text-fg-muted">{tier.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
        {priority && <p className={authLabelCls}>Agreements</p>}
        {docs.map((doc) =>
          accepted[doc] ? (
            <p key={doc} className="flex items-start gap-2 text-sm text-fg-muted">
              <span aria-hidden="true" className="text-safe-green">✓</span>
              <span>You accepted the {DOC_TITLES[doc]}.</span>
            </p>
          ) : (
            <button
              key={doc}
              type="button"
              onClick={() => setReading(doc)}
              className="w-full rounded-md border border-line px-3 py-2 text-sm text-fg hover:border-electric-blue"
            >
              Read and accept the {DOC_TITLES[doc]}
            </button>
          ),
        )}
        {!priority && (
          <p className="text-xs text-fg-faint">
            On the Self-serve plan, Phantm keeps your prompts and responses for 90 days and uses them to run and improve
            its routing, as the{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer" className="text-electric-blue hover:underline">
              Privacy Policy
            </a>{" "}
            explains. Need Phantm not to keep them?{" "}
            <a href={BOOK_A_CALL_URL} target="_blank" rel="noreferrer" className="text-electric-blue hover:underline">
              Talk to us about Enterprise
            </a>
            .
          </p>
        )}
      </div>
      <button type="submit" disabled={!canSubmit} className={authButtonCls}>
        {submitting ? "Creating your account…" : "Create account"}
      </button>
      {reading && (
        <TermsModal
          key={reading}
          title={DOC_TITLES[reading]}
          onAgree={() => {
            setAccepted((prev) => ({ ...prev, [reading]: true }));
            setReading(null);
          }}
          onCancel={() => setReading(null)}
        >
          {DOC_BODIES[reading]}
        </TermsModal>
      )}
    </AuthCard>
  );
}
